import { useState, type FormEvent } from 'react';

export type FormField = {
  name: string;
  label: string;
  type: 'text' | 'email' | 'textarea' | 'select';
  placeholder?: string;
  required?: boolean;
  options?: string[];
  maxLength?: number;
  hint?: string;
  half?: boolean;
};

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

type ReaddyFormProps = {
  formId: string;
  submitAddr: string;
  fields: FormField[];
  honeypotName: string;
  submitLabel?: string;
  successText?: string;
  note?: string;
};

type ParsedResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

const inputClass =
  'w-full bg-background-50 border border-foreground-950/15 rounded-md px-3.5 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 transition-colors focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20';

export default function ReaddyForm({
  formId,
  submitAddr,
  fields,
  honeypotName,
  submitLabel = '提交',
  successText = '已收到你的信息，我们会尽快与你联系。',
  note,
}: ReaddyFormProps) {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const trapValue = String(formData.get(honeypotName) ?? '').trim();
    if (trapValue) {
      setStatus('success');
      setMessage(successText);
      formEl.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === honeypotName) return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus('submitting');
    setMessage('');

    try {
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: ParsedResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ParsedResponse;
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setMessage(successText);
        formEl.reset();
      } else {
        setStatus('error');
        setMessage(
          serverMsg
            ? `提交失败：${serverMsg}`
            : '提交失败，请稍后重试，或直接发送邮件至 contact@shentong-baoliao.com。',
        );
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，提交未成功。请稍后重试，或直接发送邮件与我们联系。');
    }
  };

  return (
    <form
      id={formId}
      data-readdy-form={formId}
      onSubmit={handleSubmit}
      noValidate
      className="w-full"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
        {fields.map((field) => {
          const fieldId = `${formId}-${field.name}`;
          return (
            <div
              key={field.name}
              className={field.half ? 'sm:col-span-1' : 'sm:col-span-2'}
            >
              <label
                htmlFor={fieldId}
                className="block font-mono text-xs tracking-[0.12em] uppercase text-foreground-600 mb-2"
              >
                {field.label}
                {field.required ? <span className="text-primary-500 ml-1">*</span> : null}
              </label>

              {field.type === 'textarea' ? (
                <textarea
                  id={fieldId}
                  name={field.name}
                  required={field.required}
                  rows={5}
                  maxLength={field.maxLength ?? 500}
                  placeholder={field.placeholder}
                  className={`${inputClass} resize-y leading-relaxed`}
                ></textarea>
              ) : null}

              {field.type === 'select' ? (
                <select
                  id={fieldId}
                  name={field.name}
                  required={field.required}
                  defaultValue=""
                  className={`${inputClass} cursor-pointer`}
                >
                  <option value="" disabled>
                    请选择
                  </option>
                  {(field.options ?? []).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : null}

              {field.type === 'text' || field.type === 'email' ? (
                <input
                  id={fieldId}
                  name={field.name}
                  type={field.type}
                  required={field.required}
                  maxLength={field.type === 'text' ? (field.maxLength ?? 120) : undefined}
                  placeholder={field.placeholder}
                  autoComplete={field.type === 'email' ? 'email' : 'off'}
                  className={inputClass}
                />
              ) : null}

              {field.hint ? (
                <p className="mt-1.5 text-xs text-foreground-500">{field.hint}</p>
              ) : null}
            </div>
          );
        })}

        <input
          type="text"
          name={honeypotName}
          className="aux-field-offset"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 text-sm font-medium px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
          {status === 'submitting' ? '提交中…' : submitLabel}
        </button>
        {note ? <p className="text-xs text-foreground-500 leading-relaxed">{note}</p> : null}
      </div>

      {status === 'success' ? (
        <p
          role="status"
          className="mt-5 flex items-start gap-3 text-sm bg-secondary-100 text-secondary-900 border border-secondary-200 rounded-md px-4 py-3.5"
        >
          <i className="ri-checkbox-circle-line text-lg"></i>
          <span>{message}</span>
        </p>
      ) : null}

      {status === 'error' ? (
        <p
          role="alert"
          className="mt-5 flex items-start gap-3 text-sm bg-primary-100 text-primary-900 border border-primary-200 rounded-md px-4 py-3.5"
        >
          <i className="ri-error-warning-line text-lg"></i>
          <span>{message}</span>
        </p>
      ) : null}
    </form>
  );
}